import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Users, AlertTriangle } from 'lucide-react';
import Badge from '@/components/Badge';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { getRecoveryPostings } from '@/services/api';

const RecoveryBoardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getRecoveryPostings();
        setData(response.data);
      } catch (error) {
        console.error('Failed to fetch recovery data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-primary">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="bg-background border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/dashboard')}>
            <Badge className="w-8 h-8" />
            <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              CBEC
            </span>
          </div>
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard-btn">
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-accent/10 rounded-sm flex items-center justify-center mx-auto mb-6">
            <Users className="w-10 h-10 text-accent" />
          </div>
          <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            RECOVERY <span className="text-accent">BOARD</span>
          </h1>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-accent/10 border border-accent/30 rounded-sm text-accent">
            <Clock className="w-4 h-4" />
            <span className="font-semibold uppercase tracking-wide">Coming Soon</span>
          </div>
        </div>

        <div className="bg-card border border-border rounded-sm p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
            LAUNCHING AFTER FIRST CLASS GRADUATES
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            The Recovery Board will connect certified bail enforcement agents with bondsmen 
            seeking recovery services. This exclusive marketplace will be available only to 
            graduates of the Colorado Bail Enforcement Certification Course.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mt-8">
            <div className="p-6 bg-secondary/50 rounded-sm">
              <h3 className="font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>FOR AGENTS</h3>
              <ul className="text-sm text-muted-foreground space-y-2 text-left">
                <li>• Browse active recovery postings</li>
                <li>• Filter by region and bond amount</li>
                <li>• Build your professional profile</li>
                <li>• Track completed recoveries</li>
              </ul>
            </div>
            <div className="p-6 bg-secondary/50 rounded-sm">
              <h3 className="font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>FOR BONDSMEN</h3>
              <ul className="text-sm text-muted-foreground space-y-2 text-left">
                <li>• Post recovery cases</li>
                <li>• Access certified agents only</li>
                <li>• Verify agent credentials</li>
                <li>• Track case status</li>
              </ul>
            </div>
          </div>

          {data?.is_graduate ? (
            <div className="mt-8 p-4 bg-green-500/10 border border-green-500/30 rounded-sm">
              <p className="text-green-400">
                You're a certified graduate! You'll get early access when the Recovery Board launches.
              </p>
            </div>
          ) : (
            <div className="mt-8 p-4 bg-primary/10 border border-primary/30 rounded-sm">
              <p className="text-primary">
                Complete your certification to get access when the Recovery Board launches.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecoveryBoardPage;
